// Phase 5.1 — Add/Edit Customer form.
//
// Replaces the antd <Drawer> + <Form> in customer.tsx with a shadcn <Sheet> +
// FieldRow layout. Presentation only: the form model stays the parent's
// `addCustomerValue`; submit calls the parent's `onSubmit` (same
// createCustomerAction payload). The antd Form.Item validators are ported
// one-to-one into a local `validate()` gate — customer name (whitespace →
// "Invalid Customer Name", empty → "Customer Name is required") and mobile
// (nil allowed on edit, else exactly 10 digits, else "Enter the valid Mobile
// Number"). The server-side duplicate-number message (`numberExists`) is shown
// under the mobile field.
import * as React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "ui/sheet";
import { Button } from "ui/button";
import { Input } from "ui/input";
import { RadioGroup, RadioGroupItem } from "ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "ui/select";
import { FieldRow } from "../../shared/form-section";
import { cn } from "lib/utils";
import AppConstant from "../../Globals/AppConstants";
import { getUser } from "../../Utils/localStorage";
import { X } from "lucide-react";

// Date of Birth and Wedding Anniversary only ever need month + day — many
// customers don't know/give the actual year, and for DOB in particular the
// year is often more than the customer wants to disclose. Both are still
// DATEONLY columns, so a full date is required underneath; the year stored
// is a meaningless placeholder (2000, a leap year so Feb 29 is always
// storable) that getUpcomingCustomerEvents already ignores when computing
// "this year's occurrence" from month/day alone.
const MONTH_OPTIONS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const PLACEHOLDER_YEAR = 2000;
const daysInMonth = (monthIndex: number) => new Date(PLACEHOLDER_YEAR, monthIndex + 1, 0).getDate();
const monthOf = (dateStr?: string) => (dateStr ? Number(dateStr.slice(5, 7)) - 1 : undefined);
const dayOf = (dateStr?: string) => (dateStr ? Number(dateStr.slice(8, 10)) : undefined);
const buildMonthDay = (month: number, day: number) =>
  `${PLACEHOLDER_YEAR}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

function MonthDayPicker({ value, onChange }: { value?: string; onChange: (next?: string) => void }) {
  const month = monthOf(value);
  const day = dayOf(value);
  return (
    <div className="flex gap-2">
      <Select
        value={month != null ? String(month) : ""}
        onValueChange={(v) => {
          const nextMonth = Number(v);
          onChange(buildMonthDay(nextMonth, Math.min(day ?? 1, daysInMonth(nextMonth))));
        }}
      >
        <SelectTrigger className="flex-1"><SelectValue placeholder="Month" /></SelectTrigger>
        <SelectContent>
          {MONTH_OPTIONS.map((m, i) => (
            <SelectItem key={i} value={String(i)}>{m}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={day != null ? String(day) : ""}
        onValueChange={(v) => {
          onChange(buildMonthDay(month ?? 0, Number(v)));
        }}
      >
        <SelectTrigger className="flex-1"><SelectValue placeholder="Day" /></SelectTrigger>
        <SelectContent>
          {Array.from({ length: daysInMonth(month ?? 0) }, (_, i) => i + 1).map((d) => (
            <SelectItem key={d} value={String(d)}>{d}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {value && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Clear date"
          onClick={() => onChange(undefined)}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

// One mutually-exclusive tri-state instead of a switch + a conditional
// checkbox — mirrors whatsappGroupStatus/isNotWhatsappNumber on the backend.
const WHATSAPP_STATUS_OPTIONS = [
  { value: "joined", label: "WhatsApp Group Joined" },
  { value: "not_joined", label: "Not Joined to WhatsApp Group" },
  { value: "not_whatsapp", label: "Not a WhatsApp Number" },
];

function whatsappStatusValue(formData: any): string {
  if (formData?.isNotWhatsappNumber === 1) return "not_whatsapp";
  if (formData?.whatsappGroupStatus === 1) return "joined";
  return "not_joined";
}

function whatsappStatusPatch(value: string) {
  switch (value) {
    case "joined": return { whatsappGroupStatus: 1, isNotWhatsappNumber: 0 };
    case "not_whatsapp": return { whatsappGroupStatus: 0, isNotWhatsappNumber: 1 };
    default: return { whatsappGroupStatus: 0, isNotWhatsappNumber: 0 };
  }
}

export interface AddCustomerSheetProps {
  open: boolean;
  onClose: () => void;
  isEdit: boolean;
  formData: any;
  numberExists: any;
  saving: boolean;
  onChange: (patch: any) => void;
  onSubmit: () => void;
  // Credit — admin-only, only meaningful once the customer already exists.
  creditLimitInput?: string;
  onCreditLimitChange?: (value: string) => void;
  // True once this customer has ANY credit ledger row (Opening Balance,
  // Usage or Settlement) — not just an Opening Balance one — so a customer
  // who already has Usage debits can't have a backdated opening balance
  // recorded on top of them either.
  hasCreditLedgerEntries?: boolean;
  openingBalanceAmount?: string;
  onOpeningBalanceAmountChange?: (value: string) => void;
  openingBalanceRemarks?: string;
  onOpeningBalanceRemarksChange?: (value: string) => void;
  onViewCreditLedger?: () => void;
}

type Errors = { customerName?: string; mobileNumber?: string };

// "Nil" is still a valid literal mobile value (walk-in customers) — only
// numeric input is capped at 10 digits.
const MOBILE_MAX_DIGITS = 10;

// Walk-in "Nil" customers get an auto-generated placeholder mobile number of
// the form "<id>_0000..." (see apparel-api's addLeadingZerosAndAppend) instead
// of a literal "Nil" string. Editing one of these customers without touching
// the mobile field must not fail validation just because the placeholder
// isn't 10 digits — this pattern recognizes it as already-valid.
const AUTO_GENERATED_MOBILE_PATTERN = /^\d+_0*$/;

export function AddCustomerSheet({
  open,
  onClose,
  isEdit,
  formData,
  numberExists,
  saving,
  onChange,
  onSubmit,
  creditLimitInput,
  onCreditLimitChange,
  hasCreditLedgerEntries,
  openingBalanceAmount,
  onOpeningBalanceAmountChange,
  openingBalanceRemarks,
  onOpeningBalanceRemarksChange,
  onViewCreditLedger,
}: AddCustomerSheetProps) {
  const [errors, setErrors] = React.useState<Errors>({});
  const isAdmin = getUser()?.roleId == 1;

  // Ported one-to-one from the antd Form.Item validators.
  const validate = (): boolean => {
    const next: Errors = {};
    const name = formData?.customerName;
    if (name && String(name).trim() === "") next.customerName = "Invalid Customer Name";
    else if (name === undefined || name === null || name === "") next.customerName = "Customer Name is required";

    const mobile = formData?.mobileNumber;
    const sanitized = mobile && String(mobile).trim().toLowerCase();
    if (isEdit && (sanitized === "nil" || AUTO_GENERATED_MOBILE_PATTERN.test(sanitized) || /^\d{10}$/.test(sanitized))) {
      // valid
    } else if (/^\d{10}$/.test(sanitized)) {
      // valid
    } else {
      next.mobileNumber = "Enter the valid Mobile Number";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleMobileChange = (raw: string) => {
    const isAllDigits = /^\d*$/.test(raw);
    if (isAllDigits && raw.length > MOBILE_MAX_DIGITS) {
      setErrors((er) => ({ ...er, mobileNumber: `Mobile number should not exceed ${MOBILE_MAX_DIGITS} digits` }));
      return;
    }
    onChange({ mobileNumber: raw });
    setErrors((er) => ({ ...er, mobileNumber: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) onSubmit();
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) { setErrors({}); onClose(); } }}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
        <SheetHeader className="border-b px-5 py-4">
          <SheetTitle>{isEdit ? AppConstant?.editCustomer : AppConstant?.addCustomer}</SheetTitle>
        </SheetHeader>
        <form onSubmit={handleSubmit} className="flex flex-1 flex-col gap-4 overflow-y-auto p-5">
          <FieldRow label={AppConstant?.customerName} required error={errors.customerName}>
            <Input
              maxLength={35}
              value={formData?.customerName ?? ""}
              onChange={(e) => { onChange({ customerName: e.target.value }); setErrors((er) => ({ ...er, customerName: undefined })); }}
            />
          </FieldRow>

          <FieldRow label={AppConstant?.mobile} required error={errors.mobileNumber}>
            <Input
              value={formData?.mobileNumber ?? ""}
              onChange={(e) => handleMobileChange(e.target.value)}
            />
            {numberExists && <p className="mt-1 text-xs text-destructive">{numberExists}</p>}
          </FieldRow>

          {isEdit && (
            <>
              <FieldRow label="Date of Birth">
                <MonthDayPicker value={formData?.dateOfBirth} onChange={(v) => onChange({ dateOfBirth: v })} />
              </FieldRow>

              <FieldRow label="Wedding Anniversary">
                <MonthDayPicker value={formData?.weddingAnniversary} onChange={(v) => onChange({ weddingAnniversary: v })} />
              </FieldRow>

              <FieldRow label="WhatsApp Status">
                <RadioGroup
                  className="flex flex-col gap-2"
                  value={whatsappStatusValue(formData)}
                  onValueChange={(v) => onChange(whatsappStatusPatch(v))}
                >
                  {WHATSAPP_STATUS_OPTIONS.map((o) => {
                    // A customer already confirmed as joined has proven they
                    // have WhatsApp — can't jump straight to "not a WhatsApp
                    // number" from there; switch to Not Joined first.
                    const disabled = o.value === "not_whatsapp" && formData?.whatsappGroupStatus === 1;
                    return (
                      <label
                        key={o.value}
                        className={cn("flex items-center gap-2 text-sm font-normal", disabled && "cursor-not-allowed opacity-50")}
                      >
                        <RadioGroupItem value={o.value} disabled={disabled} /> {o.label}
                      </label>
                    );
                  })}
                </RadioGroup>
              </FieldRow>

              {isAdmin && (
                <div className="space-y-4 rounded-lg border p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-foreground">Credit</p>
                    {onViewCreditLedger && (
                      <button type="button" onClick={onViewCreditLedger} className="text-xs text-primary hover:underline">
                        View Credit Ledger
                      </button>
                    )}
                  </div>

                  <FieldRow label="Credit Limit" hint="Available Credit = Credit Limit − Credit Balance">
                    <Input
                      type="number"
                      min={0}
                      value={creditLimitInput ?? ""}
                      onChange={(e) => onCreditLimitChange?.(e.target.value)}
                    />
                  </FieldRow>

                  {!hasCreditLedgerEntries && (
                    <FieldRow label="Opening Balance (one-time)" hint="For pre-existing paper credit owed by this customer, entered once. Saved together with the form's Save button below.">
                      <div className="space-y-2">
                        <Input
                          type="number"
                          min={0}
                          placeholder="Amount"
                          value={openingBalanceAmount ?? ""}
                          onChange={(e) => onOpeningBalanceAmountChange?.(e.target.value)}
                        />
                        <Input
                          placeholder="Remarks (optional)"
                          value={openingBalanceRemarks ?? ""}
                          onChange={(e) => onOpeningBalanceRemarksChange?.(e.target.value)}
                        />
                      </div>
                    </FieldRow>
                  )}
                </div>
              )}
            </>
          )}

          <div className="mt-auto flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => { setErrors({}); onClose(); }}>{AppConstant?.cancel}</Button>
            <Button type="submit" disabled={saving}>{isEdit ? AppConstant?.save : AppConstant?.create}</Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

export default AddCustomerSheet;
